'use strict';

import React, {useRef, useEffect} from 'react';
import TS from '@zerolight/zlthunder-time-utils';

import {formatDate, cssClasses} from '../../../shared/utils';
import {formatNumber, formatTotalMinutes} from "../shared";
import {useCustomerInfoState} from "./data";

const AlertTimeFormat = 'ddd, MMMM Do yyyy'

function Day7Value({day7Value, day14Value, formatter}) {
	return <>
		<span className="cm-value">{formatter(day7Value) || 0}</span>{" "}
		<span className="cm-value cm-previous">[{formatter(day14Value) || 0}]</span>{" "}
		<i className={cssClasses('fas', day7Value > day14Value ? 'fa-arrow-up' : 'fa-arrow-down')}/>
	</>
}

function Usage3d({className, show3d, usage3dLife, usage3dDay7, usage3dDay14}) {
	if (!show3d) {
		return null;
	}
	return <div className={cssClasses(className, 'usage-3d')}>
		<div className="minutes-total">
			Total 3D minutes used:{" "}
			<span className="cm-value">{formatTotalMinutes(usage3dLife) || 0}</span>
		</div>
		<div className="minutes-7-day">
			3D minutes used in the last 7 days:{" "}
			<Day7Value day7Value={usage3dDay7} day14Value={usage3dDay14} formatter={formatTotalMinutes}/>
		</div>
	</div>;
}

function Usage2dGenerated({className, show2d, usage2dGeneratedLife, usage2dGeneratedDay7, usage2dGeneratedDay14}) {
	if (!show2d) {
		return null;
	}
	return <div className={cssClasses(className, "usage2d-generated")}>
		<div className="generated-total">
			Total 2D images generated: <span className="cm-value">{formatNumber(usage2dGeneratedLife)}</span>
		</div>
		<div className="generated-7-days">
			2D images generated in last 7 days:{" "}
			<Day7Value day7Value={usage2dGeneratedDay7} day14Value={usage2dGeneratedDay14} formatter={formatNumber}/>
		</div>
	</div>;
}

function Allocation3D({
	show3d, limit3d, allowance3d, alertThreshold, usage3dDay7, className,
	trialAllocation, subProject, subProjectLimit3d, subProjectAllowance3d
}) {
	if (!show3d) {
		return null;
	}
	let show3DAllowance = (usage3dDay7 !== null) && (limit3d !== null);
	if (!show3DAllowance) {
		return null;
	}
	if (subProject) {
		return <LegacyAllocation3D
			limit3d={subProjectLimit3d} allowance3d={subProjectAllowance3d}
			alertThreshold={alertThreshold} usage3dDay7={usage3dDay7} className={className}
		/>
	} else if (trialAllocation) {
		return <TrialAllocation3D {...{limit3d, allowance3d, trialAllocation, className}} />
	} else {
		return <LegacyAllocation3D {...{limit3d, allowance3d, alertThreshold, usage3dDay7, className}} />
	}
}

function TrialAllocation3D({className, limit3d, trialAllocation, allowance3d}) {
	let allowance3dMinutes = allowance3d / TS.MINUTE;
	let allowance;
	if (allowance3dMinutes > 0) {
		allowance = <div className="3d-minutes-allowance">
			Remaining minutes:{" "}
			<span className="cm-value">{formatNumber(allowance3dMinutes)}</span>
		</div>
	} else {
		allowance = <div className="3d-minutes-debt">
			Minutes used over current allowance:{" "}
			<span className="cm-value">{formatNumber(-allowance3dMinutes)}</span>
		</div>
	}

	let unallocatedMinutes = limit3d - trialAllocation;
	let unallocated = null;
	if (unallocatedMinutes > 0) {
		unallocated = <div className="3d-minutes-allowance">
			Unallocated minutes:{" "}
			<span className="cm-value">{formatNumber(unallocatedMinutes)}</span>
		</div>
	}
	return <div className={cssClasses(className)}>
		{allowance}
		{unallocated}
	</div>
}

function LegacyAllocation3D({className, allowance3d, usage3dDay7, alertThreshold}) {
	let allowance3dMinutes = allowance3d / TS.MINUTE;
	if (allowance3d > 0) {
		let consumptionRate = usage3dDay7 / (7 * TS.DAY);
		if (consumptionRate <= 0.001) {
			console.warn("consumptionRate is very low, so 3D alert is not shown ", consumptionRate);
			return null;
		}
		let estimatedAlertTime = new Date(
			Date.now() + (allowance3d / consumptionRate - alertThreshold));
		return <div className={cssClasses(className)}>
			<div className="3d-minutes-allowance">
				Remaining 3D minutes:{" "}
				<span className="cm-value">{formatNumber(allowance3dMinutes)}</span>
			</div>
			<div className="3d-alert-time">
				Estimated alert time:{" "}
				<span className="cm-value cm-date-time" data-value={estimatedAlertTime.toISOString()}>
					{formatDate(estimatedAlertTime, AlertTimeFormat)}
				</span>
			</div>
		</div>;
	} else {
		return <div className={cssClasses(className, "3d-minutes-debt")}>
			3D Minutes used over current allowance:{" "}
			<span className="cm-value">{formatNumber(-allowance3dMinutes)}</span>
		</div>;
	}

}

/**
 * 
 * @param {int} usage2dGeneratedDay7 is in miliseconds
 * @param {int} alertThreshold is in minutes; 7200 = 5 days 
 */
function Allocation2D({className, show2d, allowance2dGenerated, usage2dGeneratedDay7, alertThreshold}) {
	if (!show2d) {
		return false;
	}
	if (allowance2dGenerated > 0) {
		// let consumptionRate = usage2dGeneratedDay7 / (7 * TS.DAY);
		// if (consumptionRate <= 0.0001) {
		// 	console.warn("consumptionRate is very low, so 2D alert is not shown ", consumptionRate);
		// 	return null;
		// }
		// let estimatedAlertTime = new Date(
		// 	Date.now() + (allowance2dGenerated / consumptionRate - alertThreshold));

		let consumptionRateDay = usage2dGeneratedDay7 / (7);
		if (consumptionRateDay <= 0.01) {
			console.warn("consumptionRate by Day is very low (<0.01), so 2D alert is not shown ", consumptionRateDay);
			return null;
		}
		let alertTimeByDay = new Date(TS.addUTCDays(
			Date.now(),
			Math.floor((allowance2dGenerated / consumptionRateDay) - (alertThreshold / (60 * 24)))))

		return <div className={cssClasses(className, 'allocation-2d')}>
			<div>
				Remaining 2D images:{" "}
				<span className="cm-value">{formatNumber(allowance2dGenerated)}</span>
			</div>
			<div className="2d-alert-time">
				Estimated alert time:{" "}
				<span className="cm-value cm-date-time" data-alet-time={alertTimeByDay.toISOString()}>
					{formatDate(alertTimeByDay, AlertTimeFormat)}
				</span>
			</div>
		</div>
	} else {
		return null;
	}
}

function CustomerInfo() {
	const el = useRef(null);
	const state = useCustomerInfoState();
	let {
		stale,
		usage3dLife, usage3dDay7, usage3dDay14,
		subProject, subProjectLimit3d, subProjectAllowance3d, trialAllocation, limit3d, allowance3d,
		usage2dGeneratedLife, usage2dGeneratedDay7, usage2dGeneratedDay14,
		allowance2dGenerated, show2dGeneratedUsage,
		alertThreshold
	} = state;

	useEffect(() => {
		const dom = el.current;
		if (!dom) {
			return;
		}

		let f = a => Boolean(a);
		let gc1 = dom.querySelector(".grid-cell01");
		let gc2 = dom.querySelector(".grid-cell02");
		let gc3 = dom.querySelector(".grid-cell03");
		let gc4 = dom.querySelector(".grid-cell04");

		let cells = [gc1, gc2, gc3, gc4].filter(f);
		let c1 = [gc1, gc2].filter(f);
		let c2 = [gc3, gc4].filter(f);
		let cols = [c1, c2];

		let cleanUp = () => {
			for (let c of cells) {
				c.classList.remove("grid-cell-rowspan");
			}
		}
		cleanUp();
		for (let col of cols) {
			if (col.length === 1) {
				let d = col[0];
				d.classList.add("grid-cell-rowspan");
			}
		}
	});

	let show3d = (usage3dLife !== null);
	let show2d = (show2dGeneratedUsage && usage2dGeneratedLife !== null);

	if (stale) {
		return <div className="loading">Waiting for up-to-date metrics</div>;
	}

	if (!show3d && !show2d) {
		return null;
	}

	return (
		<section id="customer-info" ref={el}>
			<Usage3d className="grid-cell01" {...{show3d, usage3dLife, usage3dDay7, usage3dDay14}} />
			<Allocation3D
				className="grid-cell02"
				{...{
					show3d, limit3d, allowance3d, alertThreshold, usage3dDay7,
					trialAllocation, subProject, subProjectLimit3d, subProjectAllowance3d
				}}
			/>
			<Usage2dGenerated
				className="grid-cell03"
				{...{
					show2d, usage2dGeneratedLife, usage2dGeneratedDay7, usage2dGeneratedDay14
				}}
			/>
			<Allocation2D
				className="grid-cell04"
				{...{
					show2d, usage2dGeneratedLife, usage2dGeneratedDay7,
					allowance2dGenerated, alertThreshold
				}}/>
		</section>
	);
}

export default CustomerInfo;